import { collection } from "firebase/firestore";
import { db } from "firebaseConfig";
import type { FirestoreDataConverter } from "firebase/firestore";
import { toJsDate } from "utils/whatsapp/collections";
import type {
  REST_schema,
  WhatsappParamBinding,
  WhatsappTemplate,
  WhatsappTemplateButton,
  WhatsappTemplateCategory,
  WhatsappTemplateParam,
  WhatsappTemplateStatus
} from "../../../../@types/sharedSchema";
import { urlButtonParamName } from "../../../../@types/sharedSchema";

/**
 * Client-side handle + display maps for `whatsapp-templates`.
 *
 * The collection is CLIENT-READABLE so approval status flips live (Meta approval is async, up to
 * 24h — webhooks update the doc). Every query MUST carry `where("numberKey", "==", <numberKey>)`:
 * the security rule proves membership from that constraint by reading the number's access doc, and
 * rules are not filters — an unconstrained query is rejected outright.
 */

export type TemplateDoc = WhatsappTemplate & { id: string };

const withId = <T>(): FirestoreDataConverter<T> => ({
  toFirestore(value: any) {
    return value;
  },
  fromFirestore(snapshot, options) {
    return { ...snapshot.data(options), id: snapshot.id } as unknown as T;
  }
});

export const whatsappTemplatesCollection = collection(db, "whatsapp-templates").withConverter(
  withId<TemplateDoc>()
);

export type StatusTone = "success" | "warning" | "error" | "default";

/**
 * Every member of `whatsappTemplateStatuses` — a Record, not a Partial, so adding a status to the
 * shared schema is a compile error here rather than an English enum name appearing mid-Hebrew in
 * an RTL chip. Four (ARCHIVED / UNARCHIVED / REINSTATED / DELETED) used to fall through the
 * fallback and render raw.
 */
const STATUS_DISPLAY: Record<WhatsappTemplateStatus, { label: string; tone: StatusTone }> = {
  APPROVED: { label: "מאושרת", tone: "success" },
  PENDING: { label: "ממתינה לאישור", tone: "warning" },
  IN_REVIEW: { label: "ממתינה לאישור", tone: "warning" },
  REJECTED: { label: "נדחתה", tone: "error" },
  PAUSED: { label: "מושהית", tone: "warning" },
  DISABLED: { label: "חסומה", tone: "error" },
  DRAFT: { label: "טיוטה", tone: "default" },
  FLAGGED: { label: "סומנה לבדיקה", tone: "warning" },
  IN_APPEAL: { label: "בערעור", tone: "warning" },
  LIMIT_EXCEEDED: { label: "חריגה ממכסה", tone: "error" },
  LOCKED: { label: "נעולה", tone: "error" },
  PENDING_DELETION: { label: "בתהליך מחיקה", tone: "default" },
  ARCHIVED: { label: "בארכיון", tone: "default" },
  UNARCHIVED: { label: "הוחזרה מהארכיון", tone: "default" },
  REINSTATED: { label: "הופעלה מחדש", tone: "success" },
  DELETED: { label: "נמחקה", tone: "default" }
};

export const templateStatusDisplay = (
  status: WhatsappTemplateStatus
): { label: string; tone: StatusTone } => STATUS_DISPLAY[status] ?? { label: status, tone: "default" };

/**
 * Which card actions are legal for a template in this state.
 *
 * The table mirrors the SERVER's real guards, so a menu item is never offered into a guaranteed
 * 4xx (whatsappTemplates.service.ts): EDITABLE_STATUSES = APPROVED|REJECTED|PAUSED and edit also
 * needs a metaTemplateId; unpause requires PAUSED + metaTemplateId; a campaign needs Meta itself to
 * still say APPROVED, which getSendableTemplate re-checks live.
 *
 * `canDelete` is the one rule the server does NOT enforce — deleteTemplate has no status guard, so
 * a second delete re-calls Meta on an already-removed hsm and surfaces as a generic 502.
 */
export type TemplateAbilities = {
  canDuplicate: boolean;
  canDelete: boolean;
  canRefresh: boolean;
  canStats: boolean;
  canCampaign: boolean;
  canUnpause: boolean;
  /** "מקורות הערכים": where each value comes from. Webot metadata — never a Meta call. */
  canEditSources: boolean;
};

/** IMAGE/VIDEO need a stored default we can actually send; DOCUMENT/LOCATION never send at all. */
export const templateMediaIsSendable = (template: Pick<TemplateDoc, "headerFormat" | "headerMediaUrl">): boolean => {
  if (!template.headerFormat || ["NONE", "TEXT"].includes(template.headerFormat)) return true;
  if (template.headerFormat === "IMAGE" || template.headerFormat === "VIDEO") return Boolean(template.headerMediaUrl);
  return false;
};

/** A template whose header is an image or a video, so a campaign may substitute its own file. */
export const templateHasMediaHeader = (template: Pick<TemplateDoc, "headerFormat">): boolean =>
  template.headerFormat === "IMAGE" || template.headerFormat === "VIDEO";

/**
 * A template whose link param binds `contact.minisiteListingLink` — the listing-share system
 * templates. It resolves only on a send that CARRIES a listing (the matched-buyers "וואטסאפ רשמי"
 * action); from the campaign wizard or the inbox every recipient would be skipped at create, so
 * those surfaces must not offer it.
 */
export const templateNeedsListing = (template: Pick<TemplateDoc, "params">): boolean =>
  (template.params ?? []).some((param) => param.binding === "contact.minisiteListingLink");

/**
 * Webot's CURRENT listing-share templates — the server refuses to delete them
 * (TEMPLATE_SYSTEM_PROTECTED). Mirrors `LISTING_SHARE_TEMPLATE_NAMES` on the server; the retired
 * `_v1` pair (with a הסרה button) is deletable like any other template.
 */
const SYSTEM_LISTING_SHARE_TEMPLATE_NAMES: ReadonlySet<string> = new Set([
  "webot_listing_share_img_v2",
  "webot_listing_share_txt_v2"
]);
export const isSystemListingShareTemplate = (template: Pick<TemplateDoc, "name">): boolean =>
  SYSTEM_LISTING_SHARE_TEMPLATE_NAMES.has(template.name ?? "");

/**
 * May a campaign send this template — the template card's "קמפיין" action and the CRM's
 * one-dialog send both ask this.
 *
 * Deliberately stricter than status === APPROVED alone: a RED quality score is refused at send
 * (TEMPLATE_QUALITY_RED), and a media template we hold no file for — a WhatsApp Manager import — is
 * refused by getSendableTemplate. Both would otherwise be offered and dead-end at the send.
 */
export const templateIsCampaignable = (
  template: Pick<TemplateDoc, "status" | "qualityScore" | "headerFormat" | "headerMediaUrl" | "params">
): boolean =>
  template.status === "APPROVED" &&
  template.qualityScore !== "RED" &&
  templateMediaIsSendable(template) &&
  !templateNeedsListing(template);

export const templateMenuAbilities = (template: TemplateDoc): TemplateAbilities => {
  const isDraft = template.status === "DRAFT";
  const isDeleting = template.status === "PENDING_DELETION" || template.status === "DELETED";
  return {
    // Duplication reads the local doc and seeds the builder — legal in every state.
    canDuplicate: true,
    canDelete: !isDeleting && !isSystemListingShareTemplate(template),
    // A refresh asks Meta about this template by name; a draft has never been sent there.
    canRefresh: !isDraft && Boolean(template.metaTemplateId),
    // Counters key on numberKey + templateName only, so they work (as zeros) for any status.
    canStats: !isDraft,
    canCampaign: templateIsCampaignable(template),
    canUnpause: template.status === "PAUSED" && Boolean(template.metaTemplateId),
    // The listing-share pair is bound on purpose, and the server refuses it (TEMPLATE_SOURCES_SYSTEM).
    canEditSources:
      !isDeleting && !isSystemListingShareTemplate(template) && ((template.params ?? []).length > 0 || template.headerFormat === "IMAGE")
  };
};

/**
 * Newest first, on whichever timestamp the doc actually has.
 *
 * `createdTime` alone was wrong: no application code writes it — it arrives from a Cloud Function
 * as a second, asynchronous write — so a template submitted seconds ago sorts LAST (its date reads
 * as epoch 0) until that lands, which looks exactly like the button having done nothing.
 */
export const templateSortTime = (template: TemplateDoc): number =>
  toJsDate(template.createdTime)?.getTime() ??
  toJsDate(template.submittedAt)?.getTime() ??
  toJsDate(template.updatedTime)?.getTime() ??
  0;

export const TEMPLATE_CATEGORY_LABEL: Record<WhatsappTemplateCategory, string> = {
  MARKETING: "שיווקית",
  UTILITY: "תפעולית",
  AUTHENTICATION: "אימות"
};

/** Meta counts code points, not UTF-16 units — Hebrew + emoji make String.length lie. */
export const codePointLength = (text: string): number => [...text].length;

// --------------------------------------------------------------------------------------------
// Parameter value sources ("מקור הערך")
// --------------------------------------------------------------------------------------------

/**
 * What the realtor can choose as a parameter's source, grouped as they think about it.
 *
 * `listing.*` is offered since 2026-10-21: every dialog that sends a template now asks for the
 * listing when the template reads one, and fills these from it (`templateListing.ts`). Before that a
 * send carried no listing at all, and the six starter templates that bind them went out as "היי
 * לקוח/ה, עלה נכס חדש: הנכס, - חדרים, במחיר -".
 *
 * Deliberately NOT the whole `whatsappParamBindings` union: `contact.minisiteId` is hidden. It is the
 * URL-button half of the minisite pair and is set by the button editor, not by this list; picking it
 * for body text would print a bare doc id. Neither is `contact.minisiteListingLink`, the listing
 * share's own, which needs the listing put on every recipient's minisite first.
 */
export const PARAM_SOURCE_GROUPS: {
  key: "manual" | "contact" | "listing" | "realtor";
  label: string;
  options: { binding: WhatsappParamBinding; label: string; hint?: string }[];
}[] = [
  {
    key: "manual",
    label: "ידני",
    options: [
      { binding: "static", label: "ערך קבוע לכל הקמפיין", hint: "אתם ממלאים אותו פעם אחת בעת יצירת הקמפיין" },
      { binding: "perRecipient", label: "ערך שונה לכל נמען", hint: "ממלאים ידנית או מגיע מקובץ הנמענים" }
    ]
  },
  {
    key: "contact",
    label: "מכרטיס הלקוח",
    options: [
      { binding: "contact.firstName", label: "שם פרטי", hint: "השם הפרטי בלבד, גם כשבכרטיס שמור שם מלא" },
      { binding: "contact.fullName", label: "שם מלא" },
      { binding: "contact.phone", label: "טלפון" },
      { binding: "contact.interestedCity", label: "עיר ראשונה שמעניינת אותו" },
      { binding: "contact.interestedNeighborhood", label: "שכונה ראשונה שמעניינת אותו" },
      { binding: "contact.minisiteLink", label: "קישור למיני-סייט", hint: "רק ללקוחות שכבר בנינו להם מיני-סייט עם נכסים" }
    ]
  },
  {
    key: "listing",
    label: "מהנכס שייבחר בשליחה",
    options: [
      { binding: "listing.summary", label: "תיאור קצר של הנכס", hint: "חדרים, שטח, קומה וכתובת, למשל: 4 חדרים, 98 מ\"ר, קומה 3, הרצל 12, חיפה" },
      { binding: "listing.description", label: "תיאור הנכס מהמודעה", hint: "הטקסט של המודעה, בשורה אחת" },
      { binding: "listing.street", label: "כתובת (רחוב ומספר)" },
      { binding: "listing.city", label: "עיר" },
      { binding: "listing.neighborhood", label: "שכונה" },
      { binding: "listing.rooms", label: "מספר חדרים" },
      { binding: "listing.sizeSqm", label: "שטח במ\"ר" },
      { binding: "listing.price", label: "מחיר", hint: "מספר בלבד, למשל 2,450,000 — הוסיפו ש\"ח בטקסט" },
      { binding: "listing.floor", label: "קומה" },
      { binding: "listing.link", label: "קישור לנכס במיני-סייט של הלקוח", hint: "לכל לקוח הקישור שלו. מי שאין לו מיני-סייט, יקבל אחד בשליחה" }
    ]
  },
  {
    key: "realtor",
    label: "הפרטים שלכם",
    options: [
      { binding: "realtor.name", label: "שם המתווך" },
      { binding: "realtor.company", label: "שם המשרד" },
      { binding: "realtor.phone", label: "טלפון המתווך" }
    ]
  }
];

/**
 * A default value to OFFER, only where a natural Hebrew one exists.
 *
 * Deliberately absent for the name bindings. There is no generic stand-in for a person's name:
 * "היי לקוח/ה" reads as bulk mail in its first three words, which is worse for the realtor than
 * not messaging that person at all. Where nothing good exists we offer nothing, and an empty
 * default means "skip the recipients this is missing for" — which the builder says out loud.
 *
 * An area or an office name is different: "האזור שלך" is how a person would actually write it.
 */
export const FALLBACK_SUGGESTIONS: Partial<Record<WhatsappParamBinding, string>> = {
  "contact.interestedCity": "האזור שלך",
  "contact.interestedNeighborhood": "האזור שלך"
};

const PARAM_SOURCE_LABEL: Partial<Record<WhatsappParamBinding, string>> = Object.fromEntries(
  PARAM_SOURCE_GROUPS.flatMap((group) => group.options.map((option) => [option.binding, option.label]))
);

/** Display name for any binding, including the ones the picker hides. */
export const paramSourceLabel = (binding?: WhatsappParamBinding): string => {
  if (!binding) return "ערך שונה לכל נמען";
  if (binding === "contact.minisiteId") return "מזהה מיני-סייט (לכפתור קישור)";
  return PARAM_SOURCE_LABEL[binding] ?? binding;
};

// --------------------------------------------------------------------------------------------
// "מקורות הערכים" — where each value comes from, changed without resubmitting the template
// --------------------------------------------------------------------------------------------

/**
 * A URL button's variable is a SUFFIX after the button's fixed address, so only a value that is one
 * belongs there: the listing's link (the send cuts the button's address off it), the minisite's id,
 * or the realtor's own value. Any other source prints words into an address, and the full minisite
 * link is refused there outright (the server's TEMPLATE_SOURCES_INVALID).
 */
const URL_BUTTON_SOURCES: ReadonlySet<WhatsappParamBinding> = new Set<WhatsappParamBinding>(["listing.link", "static", "perRecipient"]);

export type ParamSourceGroup = (typeof PARAM_SOURCE_GROUPS)[number];

/** The sources one param may take — and always the one it has now, so the picker can show it. */
export const paramSourceGroupsFor = (opts: { urlButton: boolean; current?: WhatsappParamBinding }): ParamSourceGroup[] => {
  const groups: ParamSourceGroup[] = opts.urlButton
    ? PARAM_SOURCE_GROUPS.map((group) => ({
        ...group,
        options: [
          ...group.options.filter((option) => URL_BUTTON_SOURCES.has(option.binding)),
          ...(group.key === "contact"
            ? [{ binding: "contact.minisiteId" as const, label: "המיני-סייט של הלקוח", hint: "לכפתור שכתובתו מסתיימת במזהה המיני-סייט" }]
            : [])
        ]
      })).filter((group) => group.options.length > 0)
    : PARAM_SOURCE_GROUPS;
  const current = opts.current ?? "perRecipient";
  if (groups.some((group) => group.options.some((option) => option.binding === current))) return groups;
  return [...groups, { key: "manual", label: "המקור הנוכחי", options: [{ binding: current, label: paramSourceLabel(current) }] }];
};

/** The param a URL button's address ends in, if this one is. */
export const urlButtonOfParam = (template: Pick<TemplateDoc, "buttons">, paramName: string): Extract<WhatsappTemplateButton, { type: "URL" }> | undefined => {
  for (const button of template.buttons ?? []) {
    if (button.type === "URL" && urlButtonParamName(button) === paramName) return button;
  }
  return undefined;
};

export type TemplateSourceRow = { paramName: string; binding: WhatsappParamBinding; fallbackValue: string };

/** The dialog's starting rows — a param stored with no binding is the realtor's own value, as everywhere. */
export const templateSourceRows = (template: Pick<TemplateDoc, "params">): TemplateSourceRow[] =>
  (template.params ?? []).map((param) => ({
    paramName: param.paramName,
    binding: param.binding ?? "perRecipient",
    fallbackValue: param.fallbackValue ?? ""
  }));

/**
 * What a save sends: only the rows that CHANGED, and the header only when it changed — so a save
 * never rewrites a value the realtor did not touch (a param stored with no binding stays so).
 * `null` when there is nothing to save.
 */
export const templateSourcesRequest = (
  template: Pick<TemplateDoc, "params" | "headerFormat" | "headerMediaSource">,
  rows: readonly TemplateSourceRow[],
  headerFromListing: boolean
): REST_schema.WhatsappTemplateSourcesRequest | null => {
  const before = new Map(templateSourceRows(template).map((row) => [row.paramName, row]));
  const params = rows
    .filter((row) => {
      const was = before.get(row.paramName);
      return !was || was.binding !== row.binding || was.fallbackValue.trim() !== row.fallbackValue.trim();
    })
    .map((row) => ({ paramName: row.paramName, binding: row.binding, fallbackValue: row.fallbackValue.trim() }));
  const headerChanged = template.headerFormat === "IMAGE" && headerFromListing !== (template.headerMediaSource === "listing");
  if (params.length === 0 && !headerChanged) return null;
  return { params, ...(headerChanged ? { headerMediaSource: headerFromListing ? ("listing" as const) : null } : {}) };
};

// --------------------------------------------------------------------------------------------
// Duplicate — seeding the builder, and the content key that says whether a copy is distinct yet
// --------------------------------------------------------------------------------------------

/** Everything "שכפול" carries from the source template into a fresh builder. */
export type TemplateSeed = {
  hebrewTitle: string;
  requestedCategory: "MARKETING" | "UTILITY";
  headerText?: string;
  /** Carried as a PAIR — a copy with only one of them is approved-and-unsendable, or worse. */
  headerMediaHandle?: string;
  headerMediaUrl?: string;
  headerMediaMimeType?: string;
  /** Carried with the picture: a copy of a property template is a property template. */
  headerMediaSource?: "listing";
  bodyText: string;
  footerText?: string;
  buttons?: WhatsappTemplateButton[];
  params: WhatsappTemplateParam[];
};

export const templateSeedFrom = (template: TemplateDoc): TemplateSeed => ({
  // The title is NOT part of the content hash, so prefixing it does not by itself make the copy
  // distinct — it is purely so the two are tellable apart in the list.
  hebrewTitle: `עותק של ${template.hebrewTitle}`.slice(0, 120),
  requestedCategory: template.requestedCategory === "UTILITY" ? "UTILITY" : "MARKETING",
  headerText: template.headerFormat === "TEXT" ? template.headerText : undefined,
  // The handle is reusable: it is Meta's approval sample, and a copy of the same picture should
  // be approved against the same sample rather than re-uploaded.
  ...(template.headerFormat === "IMAGE"
    ? {
        headerMediaHandle: template.headerMediaHandle,
        headerMediaUrl: template.headerMediaUrl,
        headerMediaMimeType: template.headerMediaMimeType,
        ...(template.headerMediaSource === "listing" ? { headerMediaSource: "listing" as const } : {})
      }
    : {}),
  bodyText: template.bodyText,
  footerText: template.footerText,
  // Carried so a duplicate keeps its call-to-action, its "הסרה" button included: it is an
  // ordinary row in the copy, and the builder's checkbox reads it from there.
  buttons: template.buttons,
  params: template.params ?? []
});

// --------------------------------------------------------------------------------------------
// The "הסרה" (opt-out) button
// --------------------------------------------------------------------------------------------

/** The label the builder's checkbox adds. Same as the server's `OPT_OUT_BUTTON_TEXT`. */
export const OPT_OUT_BUTTON_TEXT = "הסרה";
const OPT_OUT_BUTTON_PAYLOAD = "WEBOT_OPT_OUT";
const OPT_OUT_LABEL_WORDS: ReadonlySet<string> = new Set(["הסר", "הסרה", "להסרה", "הסירו", "להסיר"]);

/**
 * Does this button label ask to be removed? A mirror of the server's `isOptOutButtonLabel`
 * (whatsappTemplateValidator.ts): whole words, never a substring, because "הסרטון" contains "הסר".
 */
export const isOptOutButtonLabel = (text: string): boolean =>
  text
    .normalize("NFC")
    .replace(/[֑-ׇ]/g, "")
    .replace(/[.,!?;:()"'`׳״\-–—…]/g, " ")
    .split(/\s+/)
    .some((word) => OPT_OUT_LABEL_WORDS.has(word));

/**
 * Is this the opt-out button? By its marker (a stored template) or its label (a row the realtor or
 * the checkbox just wrote): the server marks a removal label as the opt-out itself, and never adds
 * a second one beside it.
 */
export const isOptOutButton = (button: WhatsappTemplateButton): boolean =>
  button.type === "QUICK_REPLY" &&
  (button.isOptOut === true || button.payload === OPT_OUT_BUTTON_PAYLOAD || isOptOutButtonLabel(button.text));

/** The payload the server stores for a quick reply: minted from the label, never claimed. */
const storedQuickReplyPayload = (button: { text: string; payload?: string }): string =>
  isOptOutButtonLabel(button.text) ? OPT_OUT_BUTTON_PAYLOAD : button.payload && button.payload !== OPT_OUT_BUTTON_PAYLOAD ? button.payload : "";

const collapseWhitespace = (text: string): string => text.replace(/\s+/g, " ").trim();

/**
 * A byte-for-byte mirror of the server's `normalizeTemplateContent` — the string whose sha256
 * becomes both the Meta template name and our Firestore doc id.
 *
 * It exists here ONLY to answer "has this copy diverged from its source yet", locally and
 * instantly, so the builder can say so before the realtor presses submit rather than after Meta
 * returns a 409. Keep the field list and the ordering identical to
 * server/types/whatsappTemplateValidator.ts — a drift here makes the advisory wrong, never the
 * server's answer, so the failure mode is a confusing dialog rather than a bad write. A quick
 * reply's payload is read as the server STORES it (`requestButtonsToContent`), so a "הסרה" row
 * typed again matches the source's marked one.
 */
export const templateContentKey = (content: {
  requestedCategory: string;
  headerFormat?: string;
  headerText?: string;
  headerMediaUrl?: string;
  bodyText: string;
  footerText?: string;
  buttons?: WhatsappTemplateButton[];
}): string =>
  [
    "he",
    content.requestedCategory,
    collapseWhitespace(content.headerText ?? ""),
    collapseWhitespace(content.bodyText),
    collapseWhitespace(content.footerText ?? ""),
    (content.buttons ?? [])
      .map((button) => {
        switch (button.type) {
          case "QUICK_REPLY":
            return `QUICK_REPLY:${collapseWhitespace(button.text)}:${storedQuickReplyPayload(button)}`;
          case "URL":
            return `URL:${collapseWhitespace(button.text)}:${collapseWhitespace(button.url)}`;
          case "PHONE_NUMBER":
            return `PHONE_NUMBER:${collapseWhitespace(button.text)}:${button.phoneNumber}`;
          default:
            return "";
        }
      })
      .join("|"),
    // Appended CONDITIONALLY, exactly as the server does: a template with no media header must
    // produce the same six-element string it always did, or every hash written before media
    // headers existed stops matching.
    ...(content.headerMediaUrl ? [`${content.headerFormat ?? "IMAGE"}:${content.headerMediaUrl}`] : [])
  ].join("|");

export const formatHebrewDate = (value: unknown): string => {
  const date = toJsDate(value);
  if (!date) return "";
  return date.toLocaleDateString("he-IL", { day: "numeric", month: "long", year: "numeric" });
};
